import { Component, OnInit, Input, Output, EventEmitter } from "@angular/core";
import { FormControl, Validators } from "@angular/forms";
import { FiduciaryRepository } from "@fiduciaria/Shared/Repositories/fiduciary.repository";
import { RequestDetailsDocumentsService } from "@fiduciaria/Shared/Services/request-details-documents.service";
import {
  RequestFiles,
  FileDisplay,
  FilesMetadataResponse,
  BlobFile,
} from "@fiduciaria/Shared/Types/files/request-files.interface";
import { TrustRequest } from "@fiduciaria/Shared/Types/request.interface";
import { faComment, faExclamationCircle } from "@fortawesome/free-solid-svg-icons";
import { NgxFileDropEntry, FileSystemFileEntry } from "ngx-file-drop";
import { catchError, of } from "rxjs";

import { ModalTitles, ModalType } from "@shared/Constantes/modal.constantes";
import { ModalService } from "@shared/Services/modal.service";

import { Usuario } from "../../../../../shared/Entities/Usuario/usuario.entity";
import { UsuariosRepository } from "../../../../../shared/Repositories/usuarios.repository";
import { noWhitespaceAndMaxLengthValidator } from "../../Validators/no-whitespace";

@Component({
  selector: "app-request-details-documents",
  templateUrl: "./request-details-documents.component.html",
  styleUrls: ["./request-details-documents.component.scss"],
})
export class RequestDetailsDocumentsComponent implements OnInit {
  public currentUser: Usuario;
  private readonly acceptedFormat: string[] = [".pdf", "image/*", ".xls", ".xlsx", ".doc"];
  public currentAcceptedFormat: string;
  public placeHolder: string;
  public newFiles: globalThis.File[] = [];
  public uploadedFiles: globalThis.File[] = [];
  public filesDisplay: FileDisplay[] = [];
  public uploadedfilesDisplay: FileDisplay[] = [];
  public requestFiles: RequestFiles;

  public filesComment: string;
  public filesCommentMaxLength: number;
  private commentControl: FormControl;

  public uploadDocuments: boolean;
  private readonly maxFilesSizePerRequest: number = 25;
  private readonly requestCreationLabel: string = "request-creation";
  private readonly continueCaseLabel: string = "continue-case";

  @Input() public formatDate: (date: string) => string;
  @Input() public formatDateTime: (date: string, time: string) => string;

  @Input() public observationMessage: string;
  @Input() public estadoEsperaInformacionCliente: string;
  @Input() public request: TrustRequest;
  @Input() public createdByFiduciary: boolean;
  @Input() public fiduciaryNote: string;

  @Output() public fileUploaded: EventEmitter<FileDisplay[]> = new EventEmitter<FileDisplay[]>();
  @Output() public refreshRequestResult = new EventEmitter<boolean>();
  public faExclamationCircle = faExclamationCircle;
  public faComment = faComment;

  constructor(
    private readonly userRepository: UsuariosRepository,
    private readonly fiduciaryRepository: FiduciaryRepository,
    private readonly modalService: ModalService,
    private readonly requestDetailsDocumentsService: RequestDetailsDocumentsService
  ) {}

  public ngOnInit(): void {
    this.setCurrentUser();
    this.currentAcceptedFormat = this.acceptedFormat.join(",");
    this.placeHolder = "Adjuntar documento";
    this.filesComment = "";
    this.filesCommentMaxLength = 250;
    this.commentControl = new FormControl("", [
      Validators.required,
      noWhitespaceAndMaxLengthValidator(this.filesCommentMaxLength),
    ]);
    this.uploadDocuments = true;
    this.loadFiles();
  }

  public onFileDropped(files: NgxFileDropEntry[]): void {
    files.forEach((_file) => {
      if (_file.fileEntry.isFile) {
        const fileEntry: FileSystemFileEntry = _file.fileEntry as FileSystemFileEntry;
        fileEntry.file((file) => {
          this.addFile(file);
        });
      }
    });
  }

  public isCreationFile(label: string): boolean {
    label = (label ?? "").trim();
    return label === this.requestCreationLabel;
  }

  public isAdditionalFile(label: string): boolean {
    label = (label ?? "").trim();
    return label === this.continueCaseLabel;
  }

  public onCommentChange(value: string): void {
    value = value.trim();
    this.filesComment = value;
    this.commentControl.setValue(value);
  }

  private validateFilesComment(): boolean {
    if (this.commentControl.invalid) {
      const commentErrorMessage: string = `El comentario supera el límite de ${this.filesCommentMaxLength} caracteres`;
      this.alert(commentErrorMessage, ModalTitles.warning, ModalType.warning);
      return false;
    }

    return true;
  }

  public get isWaitingForInformation(): boolean {
    return (this.request?.statusCode ?? "").trim() === this.estadoEsperaInformacionCliente;
  }

  private isImage(file: File | string): boolean {
    return this.requestDetailsDocumentsService.isImage(file);
  }

  private getSizeMegaBytes(bytes: number, digits: number): number {
    return this.requestDetailsDocumentsService.getSizeMegaBytes(bytes, digits);
  }

  private setCurrentUser(): void {
    this.userRepository.getInfoClienteActual().subscribe((currentUser: Usuario) => {
      this.currentUser = currentUser;
    });
  }

  private validateRequestFilesSize(files: FileDisplay[]): {
    validate: boolean;
    size: number;
    limit: number;
    avaliable: number;
  } {
    let sizeBytes: number = 0;
    let sizeMegaBytes: number = 0;
    const limit: number = this.maxFilesSizePerRequest;

    files = files.filter((x) => x.label !== this.requestCreationLabel);
    for (const file of files) sizeBytes += file.size.bytes;

    sizeMegaBytes = this.getSizeMegaBytes(sizeBytes, 2);
    const availableSize: number = limit - sizeMegaBytes;

    return {
      validate: sizeMegaBytes <= limit,
      size: sizeMegaBytes,
      limit,
      avaliable: availableSize >= 0 ? availableSize : 0,
    };
  }

  private timeFormat(date: Date): string {
    return this.requestDetailsDocumentsService.timeFormat(date);
  }

  public dateFormat(date: Date): string {
    return this.requestDetailsDocumentsService.dateFormat(date);
  }

  public splitDateTime(dateTime: string): string[] {
    return this.requestDetailsDocumentsService.splitDateTime(dateTime);
  }

  private addFile(file: File): void {
    const currentFiles: FileDisplay[] = [...this.filesDisplay, ...this.uploadedfilesDisplay];
    const newFile: FileDisplay = {
      size: { megabytes: this.getSizeMegaBytes(file.size, 2), bytes: file.size },
    };

    const validateRequestFiles: {
      validate: boolean;
      size: number;
      limit: number;
      avaliable: number;
    } = this.validateRequestFilesSize([newFile, ...currentFiles]);

    if (!validateRequestFiles.validate) {
      this.alert(
        `El tamaño máximo permitido es ${validateRequestFiles.limit} mbs, tamaño actual de archivos ${validateRequestFiles.size} mbs`,
        "danger",
        "danger"
      );
      return;
    }

    this.filesDisplay.unshift(
      ...this.requestDetailsDocumentsService.filesToDisplayFiles([file], { label: this.continueCaseLabel })
    );

    this.newFiles.unshift(file);
  }

  private blobFilesToDisplayFiles(blobFiles: BlobFile[]): void {
    blobFiles.forEach((blobFile) => {
      const file = {
        name: blobFile.metadata["fileName"],
        type: blobFile.type,
        size: parseInt(blobFile.metadata["fileSize"]),
        url: blobFile.url,
      };

      const dateTime: string = this.formatDate(blobFile.createdOn);
      const dateTimeArray: string[] = this.splitDateTime(dateTime);
      const date: string = dateTimeArray[0];
      const time: string = dateTimeArray[1];

      this.uploadedfilesDisplay.push({
        name: file.name,
        type: file.type,
        size: {
          megabytes: this.getSizeMegaBytes(file.size, 2),
          bytes: file.size,
        },
        extension: file.type.split("/").length > 0 ? file.type.split("/")[1] : file.type,
        datetime: {
          date,
          time,
        },
        creator: {
          guid: blobFile.metadata["userGuid"],
          name: blobFile.metadata["userName"],
        },
        label: blobFile.metadata["label"],
        isNew: false,
        isImage: this.isImage(file.type),
        url: blobFile.url,
        comment: this.requestDetailsDocumentsService.base64ToString(blobFile.metadata["commentBase64"] ?? ""),
        observation: this.requestDetailsDocumentsService.base64ToString(blobFile.metadata["observationBase64"] ?? ""),
      });
    });
  }

  public checkUploadDocuments(): void {
    this.filesComment = null;
    if (!this.uploadDocuments) {
      this.filesDisplay = [];
      this.newFiles = [];
    }
  }

  public removeFile(index: number): void {
    const _filesDisplay = [...this.filesDisplay];
    if (index >= 0 && index < _filesDisplay.length) {
      const files: FileDisplay[] = _filesDisplay.splice(index, 1);

      const currentFile: FileDisplay = files[0];
      const message: string = `¿Deseas recibir remover este archivo?<br/><br/><b>${currentFile.name}</b>`;

      this.modalService
        .showConfirmCustom({
          type: ModalType.warning,
          title: ModalTitles.warning,
          body: `${message}`,
          useConfirmMessage: false,
        })
        .subscribe((ok) => {
          if (ok) {
            this.filesDisplay = _filesDisplay;
            const filesTemp: File[] = [];
            this.filesDisplay.forEach((fd) => filesTemp.push(fd.file));
            this.newFiles = filesTemp;
            if (this.newFiles.length === 0) this.filesComment = null;
          }
        });
    }
  }

  public onKeyUpRemoveFile(event: KeyboardEvent): void {
    event.preventDefault();
    event.stopPropagation();
  }

  private validateRequestNumber(requestNumber: string, isSubCaseNumber: boolean = false): boolean {
    return this.requestDetailsDocumentsService.validateRequestNumber(requestNumber, isSubCaseNumber);
  }

  private alert(body: string, title: string = ModalTitles.success, type: string = ModalType.success): void {
    this.requestDetailsDocumentsService.alert(body, title, type);
  }

  public loadFiles(): void {
    this.requestFiles = {
      request: this.request,
      files: this.filesDisplay,
      continueCase: true,
      comment: this.filesComment,
    };

    this.fiduciaryRepository.obtenerDocumentos(this.requestFiles).subscribe((data: BlobFile[]) => {
      this.blobFilesToDisplayFiles(data);
    });
  }

  private validateRequestNumbers(request: TrustRequest, errorMessage: string = ""): boolean {
    const requestNumber: string = (request?.requestNumber ?? "").trim();
    const subCaseNumber: string = (request?.subCaseNumber ?? "").trim();
    const isValidRequestNumber: boolean = this.validateRequestNumber(requestNumber);
    const isValidSubCaseNumber: boolean = this.validateRequestNumber(subCaseNumber, true);

    if (!isValidRequestNumber || !isValidSubCaseNumber) {
      let msg: string = "";
      if (!isValidRequestNumber) msg += `<br/>Número de Solicitud Invalido: ${requestNumber}`;
      if (!isValidSubCaseNumber) msg += `<br/>Número de Subcaso Invalido: ${subCaseNumber}`;

      msg = `${errorMessage}<br/>${msg}`;
      this.alert(msg, ModalTitles.danger, ModalType.danger);
      return false;
    }

    return true;
  }

  public sendResponse(): void {
    if (!this.validateFilesComment()) return;

    let message: string = `<h4>¿Estás seguro de enviar esta respuesta?</h4><br/>`;
    message += `<b>Nota:</b> Solo se puede agregar una respuesta por cada vez que la solicitud es puesta en estado <b>"En espera".</b>`;
    message += `&nbsp;Para agregar otra respuesta, tendrá que esperar a que el caso esté de nuevo en estado <b>"En espera"</b>`;
    message += `&nbsp;o ponerse en contacto a través del correo electrónico correspondiente.`;

    this.modalService
      .showConfirmCustom({
        type: ModalType.warning,
        title: ModalTitles.warning,
        body: `${message}`,
        useConfirmMessage: false,
      })
      .subscribe((ok) => {
        if (ok) {
          this.requestFiles = {
            request: this.request,
            user: this.currentUser,
            files: this.filesDisplay,
            comment: this.filesComment,
            observation: this.observationMessage,
            continueCase: true,
            label: this.continueCaseLabel,
          };

          const errorMessage: string = "Ocurrió un error al subir los documentos. Por favor, inténtelo de nuevo.";

          if (!this.validateRequestNumbers(this.request, errorMessage)) return;

          this.fiduciaryRepository
            .subirDocumentos(this.requestFiles)
            .pipe(
              catchError(() => {
                this.alert(errorMessage, ModalTitles.danger, ModalType.danger);
                return of(null);
              })
            )
            .subscribe((data: FilesMetadataResponse) => {
              const [message, title, type] = !data
                ? [errorMessage, ModalTitles.danger, ModalType.danger]
                : ["Proceso exitoso!", ModalTitles.success, ModalType.success];

              this.alert(message, title, type);
              this.refreshRequestResult.emit(true);
            });
        }
      });
  }
}
